import { getLoginInfo } from '@/utils/CryptoJS.ts'
import { NavigationGuardNext, RouteLocationNormalizedGeneric } from 'vue-router'
import { isNumberPlus, isWxBrowser } from '@/utils/is.ts'
import { getQueryParam } from '@/utils/tools'
import { useAppStore } from '@/store/modules/app.ts'
import { store } from '@/store'
import { getColumnConfig } from '@/components/live-column'
import { useAuthStore } from '@/store/modules/auth.ts'
import { useLocalStorage, useSessionStorage } from '@/hooks/web/useStorage'
import router from '@/router'
import { delUrlCode, redirectWxAuthorize } from '@/utils/wxTool.ts'
import { getMobileRouteType } from '@/utils/router-util.ts'

const appStore = useAppStore(store)
const authStore = useAuthStore(store)
const sessionStorage = useSessionStorage()
const localStorage = useLocalStorage()

/**
 * 观看端前置逻辑处理
 * 1. 加密参数处理校验
 * 2. 校验是否被拉黑
 * 3. 校验是否需要在微信浏览器打开
 * 4. 微信授权登陆
 */
router.beforeEach(
  async (to: RouteLocationNormalizedGeneric, from: RouteLocationNormalizedGeneric, next: NavigationGuardNext) => {
    console.log(`watch-before -----> from name: ${from.name as string}`)

    const routeName = to.name as string
    // 观看页面前置逻辑处理, 目前只有Mobile页面
    const liveRouteType = getMobileRouteType()
    if (liveRouteType !== 'newMobile' || routeName !== 'Mobile') {
      return next()
    }

    // 加密参数处理校验
    const paramsId = to.params.id
    if (!paramsId) {
      return next('/nopremission/9')
    }
    const clientId = sessionStorage.get('clientId')
    const roomId = sessionStorage.get('roomId')
    const columnId = sessionStorage.get('columnId')
    if (!isNumberPlus(roomId) || !isNumberPlus(clientId)) {
      return next('/nopremission/9')
    }

    // 获取直播间配置
    const notFoundCode = await appStore.fetchRoomConfig(roomId)
    if (notFoundCode) {
      return next(`/nopremission/${notFoundCode}`)
    }
    // 若 columnId 存在，并且 columnId 和 roomId 对应的登录信息都有效
    if (columnId && getLoginInfo('token', 'columnId') && getLoginInfo('token', 'roomId')) {
      return next()
    } else if (columnId) {
      // 调用 getColumnConfig 方法获取栏目配置
      await getColumnConfig()
    }
    // 校验token
    await authStore.checkToken(roomId)

    const { blocked } = authStore.userExtraInfo
    // 被拉黑
    if (blocked) {
      return next('/nopremission/0')
    }
    // 校验是否需要在微信浏览器打开
    if (!isWxBrowser() && checkNeedWxBrowserCond()) {
      return next('/nopremission/13')
    }
    const isWxAuth = localStorage.get(`wx_auth_${roomId}`)
    if (isWxAuth) {
      const wxCode: any = to.query?.code
      if (!watchDomainIsSame()) {
        return _toWatchDomainHandle(to.path)
      } else if (wxCode) {
        delUrlCode()
      }
      authStore.wxCode = localStorage.get(`wx_auth_code_${roomId}`)
      localStorage.remove(`wx_auth_${roomId}`)
    } else if (isWxBrowser() && authStore.isReLogin) {
      // 未登录，观看类型和上一次不一致，同时是微信浏览器，需要去微信授权
      return await _wxAuthRedirect(to, next)
    } else {
      authStore.wxCode = localStorage.get(`wx_auth_code_${roomId}`)
    }
    return next()
  }
)

// 校验观看域名和当前域名是否一致
const watchDomainIsSame = () => {
  return appStore.config.watchDomain === window.location.hostname
}

/**
 * 校验微信授权是否有效
 * true - 无效
 */
const wxAuthTimeValid = () => {
  // 使用正确的 getItem() 方法
  const clientId = sessionStorage.get('clientId')
  const wxAuthTime = localStorage.get(`wx_auth_time_${clientId}`)
  // 检查存储的值是否存在且为有效数字
  if (!wxAuthTime || isNaN(Number(wxAuthTime))) {
    return true
  }
  // 计算时间差（天）
  const diffTime = (new Date().getTime() - Number(wxAuthTime)) / (1000 * 3600 * 24)
  return diffTime >= 7
}

/**
 * 校验是否需要在微信浏览器打开
 */
const checkNeedWxBrowserCond = () => {
  const { authType, showWxNickname, allowVisitorLogin } = appStore.config
  // 需在微信浏览器观看条件
  const needWxBrowserCond = [
    // 微信授权和参数验证 需在微信浏览器观看
    [3, 5].includes(authType!),
    // 无限制或密码登录 且 不允许游客观看 需在微信浏览器观看
    [0, 1].includes(authType!) && !allowVisitorLogin,
    // 手机号登录且开启微信昵称验证 需在微信浏览器观看
    authType === 2 && showWxNickname === 1
  ]
  return needWxBrowserCond.some(condition => condition)
}

/**
 * 微信授权跳转
 */
const _wxAuthRedirect = async (to: RouteLocationNormalizedGeneric, next: NavigationGuardNext) => {
  // const paramsId = to.params.id
  // 获取微信配置
  await authStore.getWxAppid(sessionStorage.get('encrypt') as string)

  const wxCode: any = to.query?.code
  if (wxCode) {
    if (!watchDomainIsSame()) {
      // 有wxCode 并且 观看域名和当前域名不一致，跳转到正确观看域名
      return _toWatchDomainHandle(to.path, wxCode)
    } else {
      const roomId = sessionStorage.get('roomId')
      // 跳转回来后，去掉code，同时设置已授权
      localStorage.set(`wx_auth_${roomId}`, 1)
      localStorage.set(`wx_auth_code_${roomId}`, wxCode)
      localStorage.set(`wx_auth_time_${sessionStorage.get('clientId')}`, new Date().getTime())
      return _toWatchDomainHandle(to.path)
    }
  } else {
    if (wxAuthTimeValid()) {
      // 微信授权过期，重新授权, 跳转到获取微信授权页面
      return _toWxAuthLoginHandle(to, next)
    } else {
      // 跳转到微信授权页面、静默登陆
      const { appId, componentAppId } = authStore.wxConfig
      const redirectUri = `${appStore.config.wxAuthDomain}${to.fullPath}`
      return redirectWxAuthorize(appId, componentAppId, redirectUri, appStore.config)
    }
  }
}

/**
 * 跳转到真实观看地址
 */
const _toWatchDomainHandle = async (path: string, wxCode: string = '') => {
  const params = []
  let watchUrl = `${appStore.config.watchDomainProtocol}://${appStore.config.watchDomain}${path}`
  // 收集参数
  const paramData = getQueryParam('data')
  if (paramData) {
    params.push(`data=${encodeURIComponent(paramData)}`)
  }
  if (wxCode) {
    params.push(`code=${wxCode}`)
  }

  // 拼接参数
  if (params.length > 0) {
    watchUrl += `?${params.join('&')}`
  }
  window.location.replace(watchUrl)
}

/**
 * 跳转到微信授权登陆授权页
 */
const _toWxAuthLoginHandle = (to: RouteLocationNormalizedGeneric, next: NavigationGuardNext) => {
  const { title, shareDescription, shareIcon, wxAuthDomain } = appStore.config
  const { appId, componentAppId } = authStore.wxConfig
  const watchDomain = `${appStore.config.watchDomainProtocol}://${appStore.config.watchDomain}`
  // const currentDomain = `${window.location.protocol}//${window.location.hostname}`
  // if (currentDomain !== wxAuthDomain) {
  // const oauthParams = [
  //   `redirectUri=${wxAuthDomain}${to.path}`,
  //   `appId=${appId}`,
  //   `componentAppId=${componentAppId}`,
  //   `title=${title}`,
  //   `shareIcon=${shareIcon}`,
  //   `shareDescription=${shareDescription}`,
  //   `shareLink=${watchDomain}${to.path}`
  // ]

  const oauthParams = {
    redirectUri: `${wxAuthDomain}${to.fullPath}`,
    appId: `${appId}`,
    componentAppId: `${componentAppId}`,
    title: `${title}`,
    shareIcon: `${shareIcon}`,
    shareDescription: `${shareDescription}`,
    shareLink: `${watchDomain}${to.fullPath}`
  }
  // const wxOauthUrl = `${wxAuthDomain}/wx-oauth/${to.params.id}?${oauthParams.join('&')}`
  // window.location.replace(wxOauthUrl)
  // }
  return next({ path: '/wx-oauth/' + to.params.id, query: { ...oauthParams } })
}
